import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import clsx from 'clsx';

import { useLeaderboardStore } from '@/entities/leaderboard';
import { useSessionStore } from '@/entities/session';
import { ROUTES } from '@/shared/config';
import { formatScore, openLink } from '@/shared/lib';
import { RADIUS, Squircle } from '@/shared/ui';

import styles from './UserRankCard.module.scss';

type Period = 'week' | 'month';

/** Карточка игрока на главном экране (узел 438:48175). */
export function UserRankCard() {
  const user = useSessionStore((state) => state.user);
  const channelUrl = user?.channelUrl ?? null;
  const [period, setPeriod] = useState<Period>('week');

  const weekly = useLeaderboardStore((state) => state.weekly);
  const monthly = useLeaderboardStore((state) => state.monthly);
  const loadWeekly = useLeaderboardStore((state) => state.loadWeekly);
  const loadMonthly = useLeaderboardStore((state) => state.loadMonthly);

  useEffect(() => {
    void loadWeekly();
    void loadMonthly();
  }, [loadWeekly, loadMonthly]);

  const me = (period === 'week' ? weekly : monthly)?.me;
  const stats = { place: me?.rank ?? null, score: me?.points ?? 0 };

  return (
    <Squircle cornerRadius={RADIUS.xl} className={styles.root}>
      <h2 className={styles.username} data-testid="greeting">
        @{user?.displayName ?? user?.firstName ?? 'игрок'}
      </h2>

      <Squircle cornerRadius={RADIUS.xl} className={styles.tabs} role="tablist">
        <Squircle asChild cornerRadius={RADIUS.xl}>
          <button
            type="button"
            role="tab"
            aria-selected={period === 'week'}
            className={clsx(styles.tab, period === 'week' && styles.tabActive)}
            onClick={() => { setPeriod('week'); }}
          >
            За неделю
          </button>
        </Squircle>
        <Squircle asChild cornerRadius={RADIUS.xl}>
          <button
            type="button"
            role="tab"
            aria-selected={period === 'month'}
            className={clsx(styles.tab, period === 'month' && styles.tabActive)}
            onClick={() => { setPeriod('month'); }}
          >
            За месяц
          </button>
        </Squircle>
      </Squircle>

      <div className={styles.row}>
        <p className={styles.label} data-testid="body-text-probe">
          {period === 'week' ? 'Место недели' : 'Место месяца'}
        </p>
        <p className={clsx(styles.value, styles.valueWithIcon)}>
          <img src="/images/icons/rate.svg" alt="" aria-hidden className={styles.rateIcon} />
          {stats.place ?? '—'}
        </p>
      </div>

      <div className={styles.row}>
        <p className={styles.label}>
          {period === 'week' ? 'Очки за неделю' : 'Очки за месяц'}
        </p>
        <p className={clsx(styles.value, styles.valueWithIcon)}>
          <img src="/images/icons/fire-fill.svg" alt="" aria-hidden className={styles.fireIcon} />
          {formatScore(stats.score)}
        </p>
      </div>

      <div className={styles.ctaGroup}>
        <Squircle asChild cornerRadius={RADIUS.xl}>
          <Link to={ROUTES.rating} className={styles.cta} data-testid="rating-cta">
            Рейтинг
          </Link>
        </Squircle>

        {/* Кнопка только у неподписанного и только когда есть куда вести:
            ссылку собирает сервер из `game_setting` и отдаёт в профиле.
            Канал не настроен — зовём без кнопки, подсказка остаётся. */}
        {user?.isSubscribed === false ? (
          <>
            {channelUrl ? (
              <Squircle asChild cornerRadius={RADIUS.xl}>
                <button
                  type="button"
                  className={styles.secondaryCta}
                  data-testid="rank-card-subscribe-cta"
                  onClick={() => {
                    openLink(channelUrl);
                  }}
                >
                  Подпишись на канал
                </button>
              </Squircle>
            ) : null}
            <p className={styles.hint}>Без подписки нельзя выводить фрибеты</p>
          </>
        ) : null}
      </div>
    </Squircle>
  );
}
